//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { UniversalPostType } from '@apis/universal-posts/universalPostsApiModel';
import { getDefaultPostTypeForSocialNetwork } from '@apis/universal-posts/universalPostsUtils';
import Cuid from '@models/Cuid';
import DraftId from '@models/DraftId';
import PostableId from '@models/PostableId';
import { SocialNetwork } from '@models/SocialNetwork';
import { isDefined } from '@utils/filterPredicates';

import {
  IStateParams,
  StringifiedStateParams,
  deserializeOptionalEnumParam,
  deserializeOptionalNumericalParam,
} from 'app/initialization/routing/stateParamModels';
import { RoutableStateName } from 'app/top-level-states';

export const deserializeSchedulerParams = ({
  copiedDraftId: serializedCopiedDraftId,
  copiedScheduledPostId: serializedCopiedPostId,
  draftPostId: serializedDraftId,
  hashtag: serializedHashtag,
  postTimeMs: serializedPostTimeMs,
  postType: serializedPostType,
  scheduledPostId: serializedPostId,
  targetCuid: serializedTargetCuid,
}: StringifiedStateParams<'schedule.scheduler'>) => {
  return {
    copiedDraftId: deserializeOptionalNumericalParam<DraftId>(
      serializedCopiedDraftId,
    ),
    copiedScheduledPostId: deserializeOptionalNumericalParam<PostableId>(
      serializedCopiedPostId,
    ),
    editDraftId: deserializeOptionalNumericalParam<DraftId>(serializedDraftId),
    editPostId: deserializeOptionalNumericalParam<PostableId>(serializedPostId),
    initialCuid: isDefined(serializedTargetCuid)
      ? Cuid(serializedTargetCuid)
      : undefined,
    initialPostTimeMs: deserializeOptionalNumericalParam(serializedPostTimeMs),
    postType: deserializeOptionalEnumParam(
      UniversalPostType,
      serializedPostType,
    ),
    // When there are multiple hashtags serializedHashtag is an array instead of a string
    hashtags: isDefined(serializedHashtag)
      ? serializedHashtag.toString().split(',')
      : undefined,
  };
};

const SECONDS_TO_MILLISECONDS_CONVERSION_FACTOR = 1000;
export const getThumbnailOffsetInSeconds = (offsetInMs: number = 0) =>
  offsetInMs / SECONDS_TO_MILLISECONDS_CONVERSION_FACTOR;

export const shouldScheduleToPinterest = (
  networkFilter: SocialNetwork | 'All',
  state: RoutableStateName,
) => {
  // Pinterest's schedule/drafts is not routed through schedule/ and not set as networkFilter
  // so we need to check the state to determine if the network is Pinterest
  return (
    networkFilter === SocialNetwork.Pinterest || state.includes('pinterest')
  );
};

export const getPostTypeForQueryParams = (
  networkFilter: SocialNetwork | 'All',
  state: RoutableStateName,
  queryParams: IStateParams['schedule.scheduler'],
) => {
  const isPinterest = shouldScheduleToPinterest(networkFilter, state);
  const network = isPinterest ? SocialNetwork.Pinterest : networkFilter;

  if (isDefined(queryParams.postType)) {
    return queryParams.postType;
  }
  if (isPinterest) {
    return [getDefaultPostTypeForSocialNetwork(SocialNetwork.Pinterest)];
  }
  if (network !== 'All') {
    return [getDefaultPostTypeForSocialNetwork(network)];
  }
};
